"use client";

import * as React from "react";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import { type VariantProps } from "class-variance-authority";

import {
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormItemProps,
} from "../form";
import { cn } from "../../lib/utils";
import { toggleVariants } from "../toggle";

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants>
>({ size: "default", variant: "default" });

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, children, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn(
      "flex items-center justify-center py-1 gap-1 bg-muted text-muted-foreground rounded-md",
      className
    )}
    {...props}
  >
    <ToggleGroupContext.Provider value={{ variant, size }}>
      {children}
    </ToggleGroupContext.Provider>
  </ToggleGroupPrimitive.Root>
));

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleVariants>
>(({ className, children, variant, size, ...props }, ref) => {
  const context = React.useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        className
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroup, ToggleGroupItem };

interface FormToggleGroupProps extends Omit<
  FormItemProps<typeof ToggleGroupPrimitive.Root, string>,
  "defaultValue" | "type"
> {
  value: string;
  onChange: (value: string) => void;
  defaultValue?: string;
  options: { label: string; value: string }[];
}

export const FormToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  FormToggleGroupProps
>(({ className, value, onChange, label, options, ...props }, ref) => (
  <FormItem className={cn("space-y-1.5 w-full", className)}>
    <FormLabel>{label}</FormLabel>
    <FormControl>
      <ToggleGroup
        {...props}
        ref={ref}
        type="single"
        value={value}
        onValueChange={onChange}
        className="w-fit gap-2 flex-wrap justify-start bg-background"
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            className="px-3 py-2 border-[1.5px] text-primary font-normal rounded-lg data-[state=on]:border-primary data-[state=on]:font-semibold"
            variant="outline"
            value={option.value}
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </FormControl>
    <FormMessage />
  </FormItem>
));

FormToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;
